"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Hero() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [mouse, setMouse] = useState({
    x: 50,
    y: 50
  });
  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const handleMove = (e: MouseEvent) => {
      const rect = node.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width * 100;
      const y = (e.clientY - rect.top) / rect.height * 100;
      setMouse({
        x,
        y
      });
    };
    node.addEventListener("mousemove", handleMove);
    return () => node.removeEventListener("mousemove", handleMove);
  }, []);
  useEffect(() => {
    const lines = 4;
    const interval = setInterval(() => {
      setRevealed(prev => {
        if (prev >= lines) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 220);
    return () => clearInterval(interval);
  }, []);
  const headlineLines = [{
    text: "SUPPORTING",
    accent: false
  }, {
    text: "FAMILIES",
    accent: false
  }, {
    text: "THROUGH",
    accent: false
  }, {
    text: "TRANSITION.",
    accent: true
  }];
  return <section data-section-id="2759" id="herotestimonials" ref={sectionRef} className="bg-background text-foreground relative flex min-h-[90vh] w-full items-center justify-center overflow-hidden px-4 py-20 sm:px-8 md:px-16">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 transition-[background-position] duration-700 ease-out" style={{
      background: `radial-gradient(600px circle at ${mouse.x}% ${mouse.y}%, hsla(84, 13%, 37%, 0.15), transparent 60%)`
    }} />

      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 opacity-[0.04]" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "80px 80px"
    }} />

      {}
      <div className="absolute left-4 top-1/2 z-10 hidden -translate-y-1/2 md:block">
        <div className="font-default text-xs uppercase tracking-[0.3em] text-secondary" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)"
      }}>
          SAFE · NEUTRAL · CHILD-CENTERED
        </div>
      </div>

      {}
      <div className="absolute right-4 top-1/2 z-10 hidden -translate-y-1/2 md:block">
        <div className="font-default text-xs uppercase tracking-[0.3em] text-secondary" style={{
        writingMode: "vertical-rl"
      }}>
          ARIZONA · FAMILY SUPPORT
        </div>
      </div>

      {}
      <div className="relative z-20 mx-auto flex w-full max-w-6xl flex-col items-center text-center">
        {}
        <div className="mb-10 flex items-center gap-4 sm:mb-14">
          <span className="h-px w-8 bg-secondary/40" />
          <span className="font-default text-[10px] uppercase tracking-[0.4em] text-secondary sm:text-xs">
            Tiny to Teen Supervised Parenting & Education Services
          </span>
          <span className="h-px w-8 bg-secondary/40" />
        </div>

        {}
        <h1 className="font-default font-bold uppercase leading-[0.95] tracking-tight text-foreground">
          {headlineLines.map((line, i) => <span key={i} data-index={i} className="block overflow-hidden">
              <span className={`inline-block text-[3rem] transition-all duration-[900ms] ease-out sm:text-[5rem] md:text-[7rem] lg:text-[10rem] ${revealed > i ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"} ${line.accent ? "text-secondary" : ""}`} style={{
            transitionDelay: `${i * 80}ms`
          }}>
                {line.text}
              </span>
            </span>)}
        </h1>

        {}
        <p className={`mt-10 max-w-[600px] font-default text-lg leading-relaxed text-foreground/80 transition-all duration-1000 sm:text-xl ${revealed >= 4 ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
        transitionDelay: "200ms"
      }}>
          Providing safe, structured, and neutral supervised parenting time and family support services across Arizona. We prioritize the well-being of children while maintaining firm, professional boundaries.
        </p>

        {}
        <div className={`mt-12 transition-all duration-1000 ${revealed >= 4 ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
        transitionDelay: "400ms"
      }}>
          <Button asChild size="lg" className="h-14 rounded-lg bg-secondary px-10 font-default text-sm font-bold uppercase tracking-[0.2em] text-white shadow-md hover:bg-secondary/90">
            <Link to="/ContactPage">
              Start Intake Process
            </Link>
          </Button>
        </div>
      </div>

      {}
      <div className="absolute bottom-6 left-1/2 z-20 -translate-x-1/2">
        <div className="flex animate-pulse items-center gap-2 font-default text-[10px] uppercase tracking-[0.4em] text-secondary/50 sm:text-xs">
          <ArrowDown className="h-3 w-3" />
          <span>Professional Support</span>
        </div>
      </div>

      {}
      <div className="absolute left-4 top-6 z-10 font-default text-[10px] uppercase tracking-[0.3em] text-secondary/40 sm:left-8 sm:top-8">
        Arizona
      </div>
      <div className="absolute right-4 top-6 z-10 font-default text-[10px] uppercase tracking-[0.3em] text-secondary/40 sm:right-8 sm:top-8">
        Est. 2024
      </div>
    </section>;
}